import LinkIcon from "@mui/icons-material/Link";
import useExcelUrl from "app/useExcelUrl";
import {
  DataGridV2 as DataGrid,
  DataGridBaseBaseProps,
  DataGridLinkColumn,
  DataGridModalEditColumn,
  GridColumns,
  PageHeader,
} from "common";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import ProgramReviewUrlForm from "./ProgramReviewUrlForm";
import { ProgramReviewUrlItem } from "./programReviewUrlApi";
import useProgramReviewUrlList from "./useProgramReviewUrlList";

export interface ProgramReviewUrlListProps
  extends Omit<DataGridBaseBaseProps, "id"> {
  showHeader?: boolean;
}

export default function ProgramReviewUrlList({
  showHeader = true,
  ...props
}: ProgramReviewUrlListProps) {
  const { params, scope, editUrl, ...service } = useProgramReviewUrlList();
  const { hasPermissions, canEditItem } = useAccess();
  const excelUrl = useExcelUrl("program-review-urls");

  let columns: GridColumns<ProgramReviewUrlItem> = [
    {
      hide: !!params.review_partner_id,
      hideable: !params.review_partner_id,
      field: "review_partner.name",
      headerName: I18n.t("ProgramReviewUrl.attributes.review_partner_id"),
      renderCell: params => (
        <DataGridLinkColumn
          href={`/review-partners/${params.row?.review_partner_id}`}
          params={params}
        />
      ),
      flex: 1,
    },
    {
      hide: !!params.program_id,
      hideable: !params.program_id,
      field: "program.name",
      headerName: I18n.t("ProgramReviewUrl.attributes.program_id"),
      renderCell: params => (
        <DataGridLinkColumn
          href={`/programs/${params.row?.program_id}`}
          params={params}
        />
      ),
      flex: 1,
    },
    {
      field: "lang",
      headerName: I18n.t("ProgramReviewUrl.attributes.lang"),
      renderCell: params => (
        <DataGridModalEditColumn
          href={`${editUrl}${params.row?.id}`}
          params={params}
        />
      ),
      type: "autocomplete",
      valueOptions: [
        { value: "en", label: "Angol" },
        { value: "hu", label: "Magyar" },
      ],
      flex: 1,
    },
    {
      field: "url",
      headerName: I18n.t("ProgramReviewUrl.attributes.url"),
      flex: 2,
    },
  ];

  return (
    <>
      {showHeader && (
        <PageHeader
          avatar={<LinkIcon />}
          title={I18n.t("ProgramReviewUrl.title")}
        />
      )}
      <DataGrid
        {...service}
        columns={columns}
        id="program-review-urls"
        excelUrl={excelUrl}
        renderModalForm={props => <ProgramReviewUrlForm {...props} />}
        modalProps={{ maxWidth: "sm" }}
        {...props}
        canCreate={hasPermissions("program_review_url.create")}
        canDelete={hasPermissions([
          "program_review_url.editany",
          "program_review_url.editown",
        ])}
        canEdit={hasPermissions([
          "program_review_url.editany",
          "program_review_url.editown",
        ])}
        canDeleteRow={params => canEditItem("program_review_url", params.row)}
        canEditRow={params => canEditItem("program_review_url", params.row)}
        canBatchActions={hasPermissions([
          "program_review_url.editany",
          "program_review_url.editown",
        ])}
        canHandleTrash={hasPermissions([
          "program_review_url.editany",
          "program_review_url.editown",
        ])}
      />
    </>
  );
}
